import { useEffect, useState } from 'react';
import { api } from '../api';
import { Badge, Modal, ErrorNote } from '../components/ui.jsx';

const ROLES = [
  { value: 'MASTER_ADMIN', label: 'Master Admin' },
  { value: 'HR_MANAGER', label: 'HR Manager' },
  { value: 'SUPERVISOR', label: 'Site Supervisor' },
];
const ROLE_BADGE = { MASTER_ADMIN: 'high', HR_MANAGER: 'administrative', SUPERVISOR: 'labor' };

const emptyForm = { name: '', email: '', password: '', role: 'SUPERVISOR', siteId: '', permissions: null };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], roleDefaults: {} });
  const [modal, setModal] = useState(null); // null | 'add' | user
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api('/users').then(setUsers).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    api('/sites').then(setSites).catch(() => {});
    api('/users/meta/permissions').then(setMeta).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // The working permission set shown in the matrix: custom if present,
  // otherwise the selected role's defaults.
  const activePerms = form.permissions ?? meta.roleDefaults[form.role] ?? [];
  const isCustom = form.permissions !== null;

  const togglePerm = (key) => {
    const base = new Set(activePerms);
    base.has(key) ? base.delete(key) : base.add(key);
    setForm((f) => ({ ...f, permissions: [...base] }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const body = {
        name: form.name,
        role: form.role,
        siteId: form.role === 'SUPERVISOR' ? form.siteId : (form.siteId || null),
        permissions: form.permissions,
      };
      if (modal === 'add') {
        await api('/users', { method: 'POST', body: { ...body, email: form.email, password: form.password } });
      } else {
        if (form.password) body.password = form.password;
        await api(`/users/${modal.id}`, { method: 'PATCH', body });
      }
      setModal(null);
      load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try { await api(`/users/${u.id}`, { method: 'PATCH', body: { active: !u.active } }); load(); }
    catch (e) { setError(e.message); }
  };

  return (
    <>
      <div className="filter-bar">
        <div className="text-muted" style={{ fontSize: 13 }}>
          Each user gets a role plus, optionally, a custom permission set that overrides it.
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setModal('add'); setError(''); }}>+ Add User</button>
      </div>
      {!modal && <ErrorNote error={error} />}

      <div className="card emp-table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Access</th><th>Site</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="fw-bold">{u.name}</td>
                <td className="text-muted">{u.email}</td>
                <td><Badge kind={ROLE_BADGE[u.role]}>{ROLES.find((r) => r.value === u.role)?.label}</Badge></td>
                <td>
                  {u.role === 'MASTER_ADMIN'
                    ? <span className="site-pill">Full access</span>
                    : u.permissions
                      ? <span className="site-pill" title={u.effectivePermissions.join(', ')}>Custom · {u.effectivePermissions.length} permissions</span>
                      : <span className="text-muted" style={{ fontSize: 12 }}>Role defaults</span>}
                </td>
                <td>{u.site ? <span className="site-pill">{u.site.name}</span> : '—'}</td>
                <td><Badge kind={u.active ? 'active' : 'absent'}>{u.active ? 'Active' : 'Disabled'}</Badge></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      setForm({ name: u.name, email: u.email, password: '', role: u.role, siteId: u.siteId ?? '', permissions: u.permissions ?? null });
                      setModal(u); setError('');
                    }}>Edit</button>
                    <button className={`btn btn-sm ${u.active ? 'btn-danger' : 'btn-success'}`}
                      onClick={() => toggleActive(u)}>{u.active ? 'Disable' : 'Enable'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Add User' : `Edit ${modal?.name ?? ''}`} wide>
        <form onSubmit={save}>
          <div className="form-row">
            <div className="form-group"><label>Full Name</label>
              <input className="form-input" value={form.name} onChange={set('name')} required /></div>
            <div className="form-group"><label>Email</label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')}
                required disabled={modal !== 'add'} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Role</label>
              <select className="form-select" value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value, permissions: null }))}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></div>
            <div className="form-group">
              <label>Site restriction {form.role !== 'SUPERVISOR' && <span className="text-muted">(optional)</span>}</label>
              <select className="form-select" value={form.siteId} onChange={set('siteId')} required={form.role === 'SUPERVISOR'}>
                <option value="">{form.role === 'SUPERVISOR' ? 'Select site…' : 'All sites'}</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>{modal === 'add' ? 'Password' : 'New Password (leave blank to keep current)'}</label>
            <input className="form-input" type="password" value={form.password} onChange={set('password')}
              minLength={form.password ? 8 : undefined} required={modal === 'add'} autoComplete="new-password" />
          </div>

          {form.role !== 'MASTER_ADMIN' && (
            <>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <label style={{ margin: 0 }}>Permissions</label>
                <div className="segmented">
                  <button type="button" className={!isCustom ? 'on' : ''}
                    onClick={() => setForm((f) => ({ ...f, permissions: null }))}>Role defaults</button>
                  <button type="button" className={isCustom ? 'on' : ''}
                    onClick={() => setForm((f) => ({ ...f, permissions: [...activePerms] }))}>Custom</button>
                </div>
              </div>
              <div className="perm-grid">
                {meta.permissions.map((p) => {
                  const on = activePerms.includes(p.key);
                  return (
                    <div key={p.key}
                      className={'perm-item' + (on ? ' on' : '')}
                      style={!isCustom ? { opacity: 0.65, cursor: 'default' } : undefined}
                      onClick={() => isCustom && togglePerm(p.key)}>
                      <span className="perm-check">{on ? '✓' : ''}</span>
                      {p.label}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <ErrorNote error={error} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save User'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
